// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { VoiceProvider } from './contexts/VoiceContext';
import { ProjectProvider, useProject, syncProjectScripts } from './contexts/ProjectContext';
import { EVENTO_CAMBIO as EVENTO_CONSENTIMIENTO } from './utils/consentimiento';
import { IdiomaProvider } from './contexts/IdiomaContext';
import { PaisProvider } from './contexts/PaisContext';
import { PageLayoutProvider, usePageLayout } from './contexts/PageLayoutContext';
import { LandingWrapper } from './components/landing/DynamicLanding';
import { BACKEND_BASE } from './services/api';
import CourseProgressView from './pages/CourseProgressView';
import { useAuth } from './contexts/AuthContext';
import VoiceTestiPhone from './pages/VoiceTestiPhone';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './App.css';

// Componentes compartidos
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CookieConsent from './components/CookieConsent';
import SeoHead from './components/SeoHead';
import AceptacionModal from './components/AceptacionModal';
import DatosComplementariosModal from './components/DatosComplementariosModal';
import PopupProyecto from './components/PopupProyecto';
import AccesoExternoPage from './pages/AccesoExternoPage';
import SuplantarPage from './pages/SuplantarPage';
import SuplantacionBanner from './components/SuplantacionBanner';
import RecuperarContrasenaPage from './pages/RecuperarContrasenaPage';
import CancelarSuscripcionPage from './pages/CancelarSuscripcionPage';
import AulaLeccionPage from './pages/AulaLeccionPage';
import BajaCorreosPage from './pages/BajaCorreosPage';
import VoiceAssistantToggle from './components/VoiceAssistantToggle';

// 🆕 NUEVO: Componente de notificación de sesión expirada
import SessionExpiredNotification from './components/SessionExpiredNotification';
import NotificacionModal from './components/NotificacionModal';
import { buildBg } from './components/landing/bgHelpers';

// Páginas
import HomePage from './pages/HomePage';
import DashboardPublicoPage from './pages/DashboardPublicoPage';
import PaginaView from './pages/PaginaView';
import EvaluacionRealtimePage from './pages/EvaluacionRealtimePage';
import RetoRealtimePage from './pages/RetoRealtimePage';
import LoginPage from './pages/LoginPage';
import SsoPage from './pages/SsoPage';
import ActivarPage from './pages/ActivarPage';
import DashboardEnvioPage from './pages/DashboardEnvioPage';
import RegisterPage from './pages/RegisterPage';
import RegistroStandalone from './pages/RegistroStandalone';
import { normalizarSlugRuta, slugRutaValido } from './utils/rutasReservadas';
import DashboardPage from './pages/DashboardPage';
import CourseListPage from './pages/CourseListPage';
import CourseDetailPage from './pages/CourseDetailPage';
import LessonPage from './pages/LessonPage';
import EvaluationPage from './pages/EvaluationPage';
import VoiceTestPage from './pages/VoiceTestPage';
import ProgressPage from './pages/ProgressPage';
import CertificadosPage from './pages/CertificadosPage';
import AdminPanel from './pages/AdminPanel';
import CodigoIngresoModal from './components/CodigoIngresoModal';

import DocumentosPage from './pages/DocumentosPage';
import ConsultaAsistentePage from './pages/ConsultaAsistentePage';
import ConsultaAsistenteiPhone from './pages/ConsultaAsistenteiPhone';
import deviceDetector from './utils/deviceDetector';

// NUEVA IMPORTACIÓN PARA ANALYTICS
import DocumentAnalytics from './pages/DocumentAnalytics';

import SpeechProgressIndicator from './components/SpeechProgressIndicator';
import MentorPage from './pages/MentorPage';
import WhatsAppTrainingPage from './pages/WhatsAppTrainingPage';
import VerificarCertificadoPage from './pages/VerificarCertificadoPage';
import MisCertificadosPage from './pages/MisCertificadosPage';
import AlertasPage from './pages/AlertasPage';
import EntregasPage from './pages/EntregasPage';
import AvisoAlertas from './components/alertas/AvisoAlertas';
import MiPlanPage from './pages/MiPlanPage';

// Componente para rutas protegidas
// /mentor/:documentId → /aula/:documentId en proyectos que usan el aula.
const RedirigirAula = ({ proyecto }) => {
  const { documentId } = useParams();
  const { search } = useLocation();
  return <Navigate to={destinoAula(proyecto, documentId, new URLSearchParams(search))} replace />;
};

// /aula/… en proyectos sin aula: la misma lección en el modo mentor. Con el aula
// en /contenidos (LAAC), a /contenidos.
const RedirigirMentor = () => {
  const { documentId, modulo, leccion } = useParams();
  const params = modulo && leccion ? `?modulo=${modulo}&leccion=${leccion}` : '';
  return <Navigate to={`/mentor/${documentId}${params}`} replace />;
};

// /aula/… con el aula activa: el curso que vive en /contenidos va allá; los demás se ven aquí.
const AulaRuta = ({ proyecto }) => {
  const { documentId, modulo, leccion } = useParams();
  const aula = proyecto?.config_json?.aula || {};
  if (aula.en_contenidos && String(aula.documento_contenidos) === String(documentId)) {
    return <Navigate to={`/contenidos${modulo && leccion ? `?modulo=${modulo}&leccion=${leccion}` : ''}`} replace />;
  }
  return <AulaLeccionPage />;
};

// Dónde se ve un curso en el aula (conserva ?modulo=&leccion=).
const destinoAula = (proyecto, documentId, query) => {
  const aula = proyecto?.config_json?.aula || {};
  const m = query.get('modulo');
  const l = query.get('leccion');
  if (aula.en_contenidos && String(aula.documento_contenidos) === String(documentId)) {
    return `/contenidos${m && l ? `?modulo=${m}&leccion=${l}` : ''}`;
  }
  return `/aula/${documentId}${m && l ? `/${m}/${l}` : ''}`;
};

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const { proyecto, loading } = useProject();

  if (!token) {
    if (loading) return null;
    return <Navigate to={proyecto?.config_json?.auth_config?.sesion_expirada_destino || '/login'} replace />;
  }
  
  return children;
};

// Lo contrario: pantallas de acceso y registro, que solo tienen sentido sin
// sesión. Quien ya entró y vuelve a /login o /register no ve el formulario, va
// a donde diga el proyecto (`auth_config.destino_con_sesion`) o a la portada.
//
// Mira el usuario de la sesión, NO el token guardado: el token puede estar ahí
// y no valer —caducado, o de una sesión que no debía sobrevivir al cierre del
// navegador—, y AuthContext lo limpia en su primer efecto. Si nos guiáramos por
// el token, quien vuelve con uno viejo no podría ni abrir el formulario.
const SoloInvitados = ({ children, destino }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={destino || '/'} replace />;
  return children;
};

// Componente interno que usa useLocation
const AppContent = () => {
  const location = useLocation();
  const { proyecto, loading: proyectoLoading } = useProject();

  // Evita que el navegador restaure la posición de scroll al recargar/volver.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Al cambiar de ruta (y al recargar) ir siempre al inicio de la página,
  // salvo cuando el enlace apunta a un ancla (#seccion).
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  // Scripts/integraciones del proyecto: se inyectan según la ruta para poder
  // deshabilitarlos en modos internos (mentor/evaluación/consulta/reto/práctica).
  useEffect(() => {
    syncProjectScripts(proyecto, location.pathname, location.search);
    // Al aceptar/cambiar el consentimiento se vuelve a sincronizar: los scripts
    // de las categorías recién permitidas entran sin recargar la página.
    const alCambiar = () => syncProjectScripts(proyecto, location.pathname, location.search);
    window.addEventListener(EVENTO_CONSENTIMIENTO, alCambiar);
    return () => window.removeEventListener(EVENTO_CONSENTIMIENTO, alCambiar);
  }, [proyecto, location.pathname, location.search]);

  // Rutas donde NO queremos mostrar Header ni Footer
  const hideLayoutRoutes = ['/consulta/', '/consultaiphone/', '/mentor/', '/acceso/', '/aula/', '/suplantar/'];
  // Aula del proyecto: /aula/… directo, o /contenidos convertido en el aula del curso.
  const aulaCfg = proyecto?.config_json?.aula || {};
  const aulaEnContenidos = !!(aulaCfg.activo && aulaCfg.en_contenidos && aulaCfg.documento_contenidos);
  const shouldHideLayout = hideLayoutRoutes.some(route =>
    location.pathname.includes(route)
  ) || location.pathname === '/activar' || location.pathname.startsWith('/activar/')
    || (location.pathname === '/recuperar-contrasena' && !proyecto?.config_json?.auth_config?.recuperar_pagina)
    || location.pathname === '/baja'
    || (location.pathname === '/contenidos' && aulaEnContenidos);

  // Detectar si la landing dinámica O las secciones globales del proyecto tienen
  // header/footer propios (en cualquiera de los dos lados cuenta).
  // Página de registro independiente configurada por el proyecto (opcional).
  const paginaRegistro = proyecto?.config_json?.registro_config?.pagina_registro;
  const slugRegistro = (paginaRegistro?.activa && slugRutaValido(paginaRegistro?.slug))
    ? normalizarSlugRuta(paginaRegistro.slug)
    : null;

  // Acceso en página propia (config_json.auth_config): en vez del popup sobre la
  // landing, /login y /register pintan una página del proyecto con sus secciones.
  const authConfig = proyecto?.config_json?.auth_config || {};
  const slugLogin = authConfig.login_pagina || null;
  const slugRegistroAuth = authConfig.registro_pagina || null;
  // Igual que el acceso: el proyecto puede tener su propia página de perfil y de
  // progreso (con su hero, su diseño y sus secciones) en vez de las pantallas
  // por defecto. Sin estas claves, /perfil y /progress siguen como siempre.
  // A dónde va quien ya tiene sesión y aun así abre /login o /register.
  const destinoConSesion = authConfig.destino_con_sesion || '/';
  const slugPerfil = authConfig.perfil_pagina || null;
  // /contenidos puede ser una página del proyecto (p. ej. la del curso en LAAC).
  const slugContenidos = authConfig.contenidos_pagina || null;
  // «¿Has olvidado tu contraseña?» en una página del proyecto (con su header,
  // footer y la sección `recuperar_contrasena`) en vez de la pantalla suelta.
  const slugRecuperar = authConfig.recuperar_pagina || null;
  const slugProgreso = authConfig.progreso_pagina || null;
  const enPaginaLogin = !!slugLogin && location.pathname === '/login';
  const enPaginaRegistroAuth = !!slugRegistroAuth && location.pathname === '/register';

  // /login y /register cuentan como "home" solo mientras el acceso sea el popup
  // sobre la landing: con página propia son páginas internas normales (y por eso
  // llevan el header y el footer del sitio).
  const isHomePage = ['/', '/login', '/register'].includes(location.pathname)
    && !enPaginaLogin && !enPaginaRegistroAuth;
  const landingSecciones = proyecto?.config_json?.landing_secciones;
  const seccionesGlobales = proyecto?.config_json?.secciones_globales;
  const hasDynamicLanding = Array.isArray(landingSecciones) && landingSecciones.length > 0;
  const someHeader = (arr) => Array.isArray(arr) && arr.some(s => s.tipo === 'header' && s.visible !== false);
  const someFooter = (arr) => Array.isArray(arr) && arr.some(s => s.tipo === 'footer' && s.visible !== false);
  const dynamicHasHeader = someHeader(landingSecciones) || someHeader(seccionesGlobales);
  const dynamicHasFooter = someFooter(landingSecciones) || someFooter(seccionesGlobales);
  // Las páginas personalizables (PaginaView) avisan vía PageLayoutContext si
  // ellas mismas tienen header/footer propios, para evitar duplicación.
  const { pageHasHeader, pageHasFooter } = usePageLayout();
  // La página de registro puede pedir que no se le pongan header/footer del sitio
  // (por defecto los muestra, como cualquier otra página interna).
  const enPaginaRegistro = !!slugRegistro && location.pathname === `/${slugRegistro}`;
  // Mientras se resuelve el proyecto del dominio todavía no se sabe si trae
  // header/footer propios: pintar los de Mentoria y quitarlos después provoca
  // un parpadeo. Se esperan (HomePage tampoco pinta nada durante esa carga).
  const hideHeader = proyectoLoading || shouldHideLayout || (isHomePage && dynamicHasHeader) || pageHasHeader
    || (enPaginaRegistro && paginaRegistro?.mostrar_header === false);
  const hideFooter = proyectoLoading || shouldHideLayout || (isHomePage && dynamicHasFooter) || pageHasFooter
    || (enPaginaRegistro && paginaRegistro?.mostrar_footer === false);

  // Fondo global de la landing (solo se aplica en la landing dinámica, no en dashboard/etc.)
  const landingConfig = proyecto?.config_json?.landing_config;
  const mainStyle = (isHomePage && hasDynamicLanding && landingConfig?.color_fondo)
    ? { background: buildBg(landingConfig, undefined) }
    : undefined;

  // En /contenidos envolvemos header + contenido en el MISMO LandingWrapper que
  // usa la home (mismo fondo/fuente y min-height:100vh), para que el header quede
  // DENTRO de ese contenedor y la estructura sea idéntica a la landing. Acotado a
  // /contenidos para no alterar el resto de páginas internas.
  const wrapInternal = location.pathname === '/contenidos' && dynamicHasHeader && !shouldHideLayout;
  const lc = landingConfig || {};
  const landingImg = lc.imagen_fondo
    ? (lc.imagen_fondo.startsWith('http') ? lc.imagen_fondo : `${BACKEND_BASE}/${lc.imagen_fondo}`)
    : null;
  const MainWrap = wrapInternal ? LandingWrapper : React.Fragment;
  const mainWrapProps = wrapInternal ? {
    $font: lc.fuente_principal || undefined,
    $bg: buildBg(lc, null),
    $bgImage: landingImg,
    $bgSize: lc.tamano_fondo,
    $bgPos: lc.posicion_fondo,
    $bgRepeat: lc.repeat_fondo,
    $bgAttach: lc.attach_fondo,
    $overlay: lc.overlay_fondo,
  } : {};

  return (
    <div className="App">
      <main className="main-content" style={mainStyle}>
        {/* El header va DENTRO de .main-content (y, en /contenidos, dentro del
            LandingWrapper) para que las páginas internas tengan la misma
            estructura que la landing. */}
        <MainWrap {...mainWrapProps}>
        {/* Mantiene el título de la pestaña al día al navegar dentro de la SPA. */}
        <SeoHead />
        {/* Aviso en pantalla de las alertas que crea una acción del alumno (también en el modo Mentor). */}
        <AvisoAlertas />
        {!hideHeader && <Header />}

        <Routes>
          {/* Rutas públicas - login/register ahora son modal overlay sobre la landing */}
          <Route path="/" element={<HomePage />} />
          <Route
            path="/login"
            element={(
              <SoloInvitados destino={destinoConSesion}>
                {slugLogin ? <PaginaView slug={slugLogin} /> : <HomePage autoAuth="login" />}
              </SoloInvitados>
            )}
          />
          <Route
            path="/register"
            element={(
              <SoloInvitados destino={destinoConSesion}>
                {slugRegistroAuth ? <PaginaView slug={slugRegistroAuth} /> : <HomePage autoAuth="register" />}
              </SoloInvitados>
            )}
          />
          <Route path="/voice-test" element={<VoiceTestPage />} />
          {/* Aterrizaje del SSO desde eventos.ateneo.co: /sso?t=<ticket> */}
          <Route path="/sso" element={<SsoPage />} />
          {/* Entrada desde otra plataforma: /acceso/<slug>/<token>?full_name=…&email=… */}
          <Route path="/acceso/:slug/:token" element={<AccesoExternoPage />} />
          {/* «Entrar como» un usuario: pase de un solo uso que abre el admin desde Usuarios */}
          <Route path="/suplantar/:pase" element={<SuplantarPage />} />
          {/* «¿Has olvidado tu contraseña?»: pedir el enlace, y con ?token= crear la nueva.
              Sin guardia de invitado: el enlace del correo tiene que abrir aunque haya sesión. */}
          {/* Enlace del recordatorio de vencimiento: cancelar la renovación sin iniciar sesión. */}
          <Route path="/cancelar-suscripcion" element={<CancelarSuscripcionPage />} />
          <Route path="/recuperar-contrasena" element={slugRecuperar ? <PaginaView slug={slugRecuperar} /> : <RecuperarContrasenaPage />} />
          {/* Darse de baja de los correos del proyecto: /baja?id=<envío>&t=<firma> */}
          <Route path="/baja" element={<BajaCorreosPage />} />
          {/* Activación web de T&C (Axon): /activar/:token (enlace único) o /activar (QR genérico) */}
          <Route path="/activar" element={<ActivarPage />} />
          <Route path="/activar/:token" element={<ActivarPage />} />
          {/* Dashboard en vivo del envío de un contenido (Axon) */}
          <Route path="/dashboard-envio" element={<DashboardEnvioPage />} />
          {/* Dashboard público de programa (acceso por correo, sin login) */}
          <Route path="/dashboard/:token" element={<DashboardPublicoPage />} />
          
          {/* Rutas protegidas */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/courses" 
            element={
              <ProtectedRoute>
                <CourseListPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/courses/:id" 
            element={
              <ProtectedRoute>
                <CourseProgressView />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/courses/:id" 
            element={
              <ProtectedRoute>
                <CourseDetailPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/lessons/:id" 
            element={
              <ProtectedRoute>
                <LessonPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/evaluations/:moduleId" 
            element={
              <ProtectedRoute>
                <EvaluationPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/progress" 
            element={
              <ProtectedRoute>
                {slugProgreso ? <PaginaView slug={slugProgreso} /> : <ProgressPage />}
              </ProtectedRoute>
            } 
          />
          <Route
            path="/perfil"
            element={<ProtectedRoute>{slugPerfil ? <PaginaView slug={slugPerfil} /> : <CertificadosPage />}</ProtectedRoute>}
          />
          <Route path="/certificados" element={<ProtectedRoute><MisCertificadosPage /></ProtectedRoute>} />
          <Route path="/alertas" element={<ProtectedRoute><AlertasPage /></ProtectedRoute>} />
          <Route path="/entregas" element={<ProtectedRoute><EntregasPage /></ProtectedRoute>} />
          <Route path="/mi-plan" element={<ProtectedRoute><MiPlanPage /></ProtectedRoute>} />
          <Route path="/verificar/:codigo" element={<VerificarCertificadoPage />} />
          <Route 
            path="/admin-panel/*"
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contenidos"
            element={
              <ProtectedRoute>
                {/* Con auth_config.contenidos_pagina, /contenidos pinta esa página (LAAC: la del curso). */}
                {slugContenidos ? <PaginaView slug={slugContenidos} />
                  : aulaEnContenidos ? <AulaLeccionPage /> : <DocumentosPage />}
              </ProtectedRoute>
            }
          />
          {/* Páginas personalizables (Fase C) — el backend gatea login/audiencia */}
          <Route path="/p/:slug" element={<PaginaView />} />

          {/* Evaluación Realtime (Fase H3) */}
          <Route
            path="/evaluacion/:documentId"
            element={
              <ProtectedRoute>
                <EvaluacionRealtimePage />
              </ProtectedRoute>
            }
          />
          {/* Reto Realtime */}
          <Route
            path="/reto/:documentId"
            element={
              <ProtectedRoute>
                <RetoRealtimePage />
              </ProtectedRoute>
            }
          />
          <Route path="/documentos" element={<Navigate to="/contenidos" replace />} />

<Route
  path="/mentor/:documentId"
  element={
    <ProtectedRoute>
      {/* Proyectos con aula (config_json.aula.reemplazar_mentor): el curso se ve
          en la vista de lección tipo LearnDash en vez del modo mentor. */}
      {proyecto?.config_json?.aula?.activo && proyecto?.config_json?.aula?.reemplazar_mentor
        ? <RedirigirAula proyecto={proyecto} />
        : <MentorPage />}
    </ProtectedRoute>
  }
/>

{/* Aula: vista de una lección (curso con lecciones en video). */}
<Route path="/aula/:documentId" element={<ProtectedRoute>{aulaCfg.activo ? <AulaRuta proyecto={proyecto} /> : <RedirigirMentor />}</ProtectedRoute>} />
<Route path="/aula/:documentId/:modulo/:leccion" element={<ProtectedRoute>{aulaCfg.activo ? <AulaRuta proyecto={proyecto} /> : <RedirigirMentor />}</ProtectedRoute>} />

<Route
  path="/consulta/:documentId"
  element={
    <ProtectedRoute>
      {/* Detección automática de iOS + parámetro forceios para forzar manualmente */}
      {deviceDetector.isIOS() || new URLSearchParams(window.location.search).get('forceios') === '1' ? (
        <ConsultaAsistenteiPhone />
      ) : (
        <ConsultaAsistentePage />
      )}
    </ProtectedRoute>
  }
/>

{/* Ruta directa para forzar vista iOS */}
<Route
  path="/consultaiphone/:documentId"
  element={
    <ProtectedRoute>
      <ConsultaAsistenteiPhone />
    </ProtectedRoute>
  }
/>

          {/* NUEVA RUTA PARA ANALYTICS DEL DOCUMENTO */}
          <Route 
            path="/analytics/:documentId" 
            element={
              <ProtectedRoute>
                <DocumentAnalytics />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/wa-training"
            element={
              <ProtectedRoute>
                <WhatsAppTrainingPage />
              </ProtectedRoute>
            }
          />

          {/* Redirigir rutas legacy de admin a panel unificado */}
          <Route path="/admin/documentos" element={<Navigate to="/admin-panel" replace />} />
          <Route path="/admin/voice-service" element={<Navigate to="/admin-panel" replace />} />
<Route path="/test-voice-iphone" element={<VoiceTestiPhone />} />
          {/* Página de registro independiente: el proyecto la activa y elige la
              URL desde el admin (Login y registro). Va antes del catch-all, y
              como es una ruta estática le gana a /:slug igual. */}
          {slugRegistro && (
            <Route
              path={`/${slugRegistro}`}
              element={<RegistroStandalone mostrarLogin={paginaRegistro?.mostrar_login !== false} />}
            />
          )}
          {/* Catch-all para páginas personalizables: /:slug → PaginaView. Va al
              final para que las rutas explícitas (/login, /perfil, /admin...) ganen. */}
          <Route path="/:slug" element={<PaginaView />} />
          {/* Artículo de un hub de contenido: /actualizacion/:articulo pinta la misma
              página del hub y su sección `articulos` muestra el artículo. */}
          <Route path="/:slug/:articulo" element={<PaginaView />} />
        </Routes>
        </MainWrap>
        <ToastContainer position="top-right" autoClose={3000} />
        <CodigoIngresoModal />
      </main>
      
     {/* <VoiceAssistantToggle />
      <SpeechProgressIndicator />*/}
      
      {/* 🆕 NUEVO: Componente de notificación de sesión expirada */}
      <SessionExpiredNotification />
      <NotificacionModal />
      
      {/* Footer condicional - NO se muestra en rutas de consulta ni cuando landing dinámica tiene footer */}
      {!hideFooter && <Footer />}

      {/* Banner de cookies del proyecto (apagado salvo que se active en el admin) */}
      <CookieConsent />

      {/* Aceptación obligatoria de términos tras iniciar sesión (por proyecto) */}
      <AceptacionModal />
      {/* Pide los datos que faltaron en el registro; apagado salvo que el proyecto lo active. */}
      <DatosComplementariosModal />

      {/* Pop up por país del proyecto (apagado salvo que se active en el admin) */}
      <PopupProyecto />

      {/* Barra fija mientras un admin está «dentro como» un usuario */}
      <SuplantacionBanner />
    </div>
  );
};

// Connects IdiomaProvider to AuthContext user + project default language
const IdiomaProviderConnector = ({ children }) => {
  const { user } = useAuth();
  const { proyecto } = useProject();
  const projectIdioma = proyecto?.config_json?.idioma_default || null;
  return (
    <IdiomaProvider userIdioma={user?.idioma_preferido} projectIdioma={projectIdioma}>
      {children}
    </IdiomaProvider>
  );
};

function App() {
  return (
    <Router>
      <ProjectProvider>
        <AuthProvider>
          <IdiomaProviderConnector>
            <PaisProvider>
              <VoiceProvider>
                <PageLayoutProvider>
                  <AppContent />
                </PageLayoutProvider>
              </VoiceProvider>
            </PaisProvider>
          </IdiomaProviderConnector>
        </AuthProvider>
      </ProjectProvider>
    </Router>
  );
}

export default App;
